import type { Store } from '@warp-drive/core';
import type { OpenSearchResponse } from 'tio-data/data/resources/search.ext';
import { executeSearchRequest } from 'tio-data/requests/search';
import type PartnerService from 'tio-employee/services/partner';
import type { DashboardContext } from 'tio-employee/utils/partner-dashboard/registry';

/** Contributions are summed over this many trailing days. */
export const SLR_WINDOW_DAYS = 30;

/** Company-wide student loan repayment activity for the program card. */
export interface SlrProgramSummary {
  /** Active, registered employees enrolled in a contribution plan. */
  participants: number;
  /** Participants who have received at least one contribution. */
  receiving: number;
  /** Cents of contributions paid in the last `SLR_WINDOW_DAYS` days, as recorded in search. */
  contributedCents: number;
}

type Count = { doc_count?: number; docCount?: number };

/**
 * The company's student loan repayment program at a glance, or null when the
 * company has no contribution plan (the card then renders nothing). Two
 * size-0 searches over the SLR datasets, aggregations only: participant docs
 * carry loan account numbers, so no documents are fetched.
 *
 * The contribution total covers contribution line items (not resends or
 * refunds) by payment date, excluding canceled transactions. It runs low:
 * transactions without a line item aren't indexed, and the index refreshes by
 * sweep, so the card labels it as recorded rather than authoritative.
 *
 * @throws (rejects) when the plans lookup or a search fails
 */
export async function loadSlrProgram(ctx: DashboardContext): Promise<SlrProgramSummary | null> {
  if (!ctx.companyId) return null;
  const partner = ctx.owner.lookup('service:partner') as PartnerService;
  const plans = await partner.loadPlansForCurrentCompany();
  if (!partner.doPlansHaveContributions(plans ?? [])) return null;

  const store = ctx.owner.lookup('service:store') as Store;
  const search = async (modelType: string, body: Record<string, unknown>) =>
    (
      await store.request<OpenSearchResponse>(
        executeSearchRequest({ 'model-type': modelType, 'search-body': body })
      )
    ).content;

  const [participants, transactions] = await Promise.all([
    search('EmployeePlan', {
      size: 0,
      track_total_hits: true,
      query: {
        bool: {
          filter: [
            { term: { slr_contribution_plan_participant_company_id_keyword: ctx.companyId } },
            { term: { slr_contribution_plan_participant_employee_is_active_boolean: true } },
            // Pending (invited, unregistered) and deactivated enrollments aren't participating yet.
            {
              term: {
                slr_contribution_plan_participant_enrollment_status_keyword: 'participating',
              },
            },
          ],
        },
      },
      aggs: {
        receiving: {
          filter: { term: { slr_contribution_plan_participant_state_keyword: 'active' } },
        },
      },
    }),
    search('AccountingTransaction', {
      size: 0,
      query: {
        bool: {
          filter: [
            { term: { slr_transaction_company_id_keyword: ctx.companyId } },
            { term: { slr_transaction_line_item_family_keyword: 'contribution' } },
            {
              range: {
                slr_transaction_payment_date_date: { gte: `now-${SLR_WINDOW_DAYS}d`, lte: 'now' },
              },
            },
          ],
          must_not: [{ term: { slr_transaction_state_keyword: 'CANCELED' } }],
        },
      },
      aggs: { paid: { sum: { field: 'slr_transaction_amount_integer' } } },
    }),
  ]);

  const receiving = (participants.aggregations as Record<string, unknown> | undefined)?.[
    'receiving'
  ] as Count | undefined;
  const paid = (transactions.aggregations as Record<string, unknown> | undefined)?.['paid'] as
    { value?: number | null } | undefined;
  const total = participants.hits?.total as { value?: number } | number | undefined;
  return {
    participants: typeof total === 'number' ? total : (total?.value ?? 0),
    receiving: receiving?.doc_count ?? receiving?.docCount ?? 0,
    contributedCents:
      typeof paid?.value === 'number' && Number.isFinite(paid.value) ? paid.value : 0,
  };
}
